import { Bell, type LucideIcon, Monitor, ShoppingCart, Wrench } from 'lucide-react';
import { itemsOf, type NavArea, visibleItem } from '../navigation.ts';

/** Saudação pela hora de quem abre o Início (no fuso do navegador dela). */
export function greetingKey(
  now: Date,
): 'home.greetingMorning' | 'home.greetingAfternoon' | 'home.greetingEvening' {
  const hour = now.getHours();
  if (hour >= 5 && hour < 12) return 'home.greetingMorning';
  if (hour >= 12 && hour < 18) return 'home.greetingAfternoon';
  return 'home.greetingEvening';
}

/**
 * Serviço da faixa "O que você precisa?" (revisão de 29/10/2026 do ADR 0077): leva ao formulário de
 * abertura que a área já tem. Só aparece se a pessoa pode abrir aquela tela, pela mesma regra do
 * menu; nunca leva à fila de quem atende. DP e RH entram depois do PR #484; Contas a Pagar, quando
 * houver um fluxo para o colaborador.
 */
export interface HomeService {
  id: 'it' | 'facilities' | 'purchasing';
  to: string;
  icon: LucideIcon;
}

const SERVICES: HomeService[] = [
  { id: 'it', to: '/it/tickets/new', icon: Monitor },
  { id: 'facilities', to: '/facilities/new', icon: Wrench },
  { id: 'purchasing', to: '/purchasing/new', icon: ShoppingCart },
];

export function homeServices(areas: NavArea[]): HomeService[] {
  return SERVICES.filter((s) => visibleItem(areas, s.to));
}

/** "Minhas solicitações" de cada área: a tela da própria área, sem somar nada por fora. */
const MY_REQUESTS = [
  '/it/tickets/mine',
  '/facilities/requests',
  '/purchasing/requests',
  '/legal/requests',
];

export interface MyRequests {
  to: string;
  icon: LucideIcon;
  /** Nome do departamento (chave de tradução). */
  areaKey: string;
}

export function myRequests(areas: NavArea[]): MyRequests[] {
  const list: MyRequests[] = [];
  for (const to of MY_REQUESTS)
    for (const area of areas)
      for (const item of itemsOf(area))
        if (item.to === to) list.push({ to, icon: item.icon, areaKey: area.key });
  return list;
}

/**
 * Linha do "Meu dia a dia". As contagens vêm das mesmas consultas das telas de destino; enquanto
 * carrega ou se falhar, a linha diz isso, nunca um número inventado.
 */
export interface DailyItem {
  id: 'approvals' | 'notifications' | 'assets' | 'contracts' | 'myProfile' | 'orgChart';
  to: string;
  icon: LucideIcon;
  count?: 'approvals' | 'notifications' | 'assets';
}

export function homeDaily(areas: NavArea[]): DailyItem[] {
  const list: DailyItem[] = [];
  const add = (id: DailyItem['id'], to: string, count?: DailyItem['count']) => {
    const item = visibleItem(areas, to);
    if (item) list.push({ id, to, icon: item.icon, ...(count ? { count } : {}) });
  };
  add('approvals', '/approvals', 'approvals');
  // Os avisos pessoais (o sino) são de todos: cada pessoa vê só os seus.
  list.push({ id: 'notifications', to: '/notifications', icon: Bell, count: 'notifications' });
  add('assets', '/assets/mine', 'assets');
  // Contratos: só o caminho; a contagem fica na tela do Jurídico.
  add('contracts', '/legal/my-contracts');
  add('myProfile', '/me');
  add('orgChart', '/people/org-chart');
  return list;
}
